/**
 * 大模型配置接口
 */
export interface AISettings {
  protocol: 'ollama' | 'openai';
  baseUrl?: string;
  apiKey?: string;
  model: string;
  options: {
    contextSize: number;
    [key: string]: any;
  };
}

/**
 * 聊天消息结构
 */
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
}

export type StreamCallback = (chunk: string) => void;

/**
 * 大模型通信客户端
 * 动态支持从环境变量或请求参数中读取默认模型请求地址
 */
export class AIClient {
  constructor(private settings: AISettings) {}

  /**
   * 发起对话请求并处理流式响应
   * @param messages 聊天上下文消息列表
   * @param onChunk 流式数据块回调函数
   */
  public async chat(messages: ChatMessage[], onChunk?: StreamCallback): Promise<string> {
    // 动态回退机制：若 settings 未传 baseUrl，则读取环境变量中的默认地址
    const defaultOllamaUrl = process.env.OLLAMA_BASE_URL || 'http://127.0.0.1:11434';
    const defaultOpenAiUrl = process.env.OPENAI_BASE_URL || 'https://api.openai.com/v1';

    const rawBaseUrl =
      this.settings.baseUrl ||
      (this.settings.protocol === 'ollama' ? defaultOllamaUrl : defaultOpenAiUrl);

    const host = rawBaseUrl.replace('localhost', '127.0.0.1').replace(/\/$/, '');

    const targetUrl =
      this.settings.protocol === 'ollama' ? `${host}/api/chat` : `${host}/chat/completions`;

    const body =
      this.settings.protocol === 'ollama'
        ? {
            model: this.settings.model,
            messages: messages,
            stream: !!onChunk,
            options: {num_ctx: this.settings.options.contextSize || 32768},
          }
        : {
            model: this.settings.model,
            messages: messages,
            stream: !!onChunk,
          };

    const res = await fetch(targetUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.settings.apiKey ? {Authorization: `Bearer ${this.settings.apiKey}`} : {}),
      },
      body: JSON.stringify(body),
    });

    if (!res.ok || !res.body) {
      throw new Error(`AI 服务响应错误: 状态码 ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullText = '';

    while (true) {
      const {done, value} = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, {stream: true});
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const json = JSON.parse(trimmed);
          const chunk = json.message?.content || json.choices?.[0]?.delta?.content || '';
          if (chunk) {
            fullText += chunk;
            if (onChunk) onChunk(chunk);
          }
        } catch (e) {}
      }
    }

    return fullText;
  }
}
